import * as Y from 'yjs';

/**
 * Y.Doc shape for a Glue graph.
 *
 *   yDoc
 *     ├── meta:        Y.Map { schemaVersion, hydratedAt, nextLegacyIntId, name }
 *     ├── nodes:       Y.Map<UUID, Y.Map>
 *     ├── connections: Y.Map<UUID, Y.Map>
 *     ├── variables:   Y.Map<name, Y.Map>
 *     └── functions:   Y.Map<funcId, Y.Map>
 *
 * Each function entry holds its own nested `nodes` / `connections` Y.Maps plus
 * ordered `inputPortDefinitions` / `outputPortDefinitions` Y.Arrays.
 *
 * Selection state and inPorts/outPorts are NOT stored in the doc:
 *   - `selected` lives in awareness state (per user)
 *   - `inPorts` / `outPorts` are derived from node templates + portUserDefinedTypes
 */

export const NODES_KEY = 'nodes';
export const CONNECTIONS_KEY = 'connections';
export const VARIABLES_KEY = 'variables';
export const FUNCTIONS_KEY = 'functions';
export const META_KEY = 'meta';

export const META_SCHEMA_VERSION = 'schemaVersion';
export const META_HYDRATED_AT = 'hydratedAt';
export const META_NEXT_LEGACY_INT_ID = 'nextLegacyIntId';
export const META_NAME = 'name';

export const NODE_ID = 'id';
export const NODE_LEGACY_INT_ID = 'legacyIntId';
export const NODE_NODE_ID = 'nodeId';
export const NODE_NODE_VERSION = 'nodeVersion';
export const NODE_POSITION = 'position';
export const NODE_POSITION_X = 'x';
export const NODE_POSITION_Y = 'y';
export const NODE_WIDTH = 'width';
export const NODE_HEIGHT = 'height';
export const NODE_SELECTED_INTEGRATION_ACCESS = 'selectedIntegrationAccess';
export const NODE_SELECTED_VARIABLE = 'selectedVariable';
export const NODE_FUNCTION_ID = 'functionId';
export const NODE_PORT_VALUES = 'portValues';
export const NODE_PORT_USER_DEFINED_TYPES = 'portUserDefinedTypes';
export const NODE_STATE = 'state';

export const CONNECTION_ID = 'id';
export const CONNECTION_LEGACY_INT_ID = 'legacyIntId';
export const CONNECTION_SENDING_NODE_ID = 'sendingNodeInstanceId';
export const CONNECTION_SENDING_PORT_NAME = 'sendingPortName';
export const CONNECTION_SENDING_PORT_TYPE = 'sendingPortType';
export const CONNECTION_RECEIVING_NODE_ID = 'receivingNodeInstanceId';
export const CONNECTION_RECEIVING_PORT_NAME = 'receivingPortName';
export const CONNECTION_RECEIVING_PORT_TYPE = 'receivingPortType';

export const VARIABLE_NAME = 'name';
export const VARIABLE_TYPE_DETERMINATION = 'typeDetermination';
export const VARIABLE_TYPE = 'type';
export const VARIABLE_RESOLVED_TYPE = 'resolvedType';

export const FUNCTION_ID = 'id';
export const FUNCTION_NAME = 'name';
export const FUNCTION_DESCRIPTION = 'description';
export const FUNCTION_NODES = 'nodes';
export const FUNCTION_CONNECTIONS = 'connections';
export const FUNCTION_INPUT_PORT_DEFINITIONS = 'inputPortDefinitions';
export const FUNCTION_OUTPUT_PORT_DEFINITIONS = 'outputPortDefinitions';
export const FUNCTION_INPUTS_NODE_ID = 'functionInputsNodeId';
export const FUNCTION_OUTPUTS_NODE_ID = 'functionOutputsNodeId';
export const FUNCTION_CREATED_AT = 'createdAt';
export const FUNCTION_UPDATED_AT = 'updatedAt';
export const FUNCTION_VERSION = 'version';

/**
 * Transaction origins. Use these as the second argument to `yDoc.transact(fn, origin)`
 * so that UndoManager and observers can distinguish the source of each change.
 *
 *   - A user's own client uses its Y.Doc clientID as origin (handled by the local
 *     dispatch hook); only that origin is in the user's UndoManager.trackedOrigins.
 *   - AGENT_ORIGIN: writes from an in-process AI agent or MCP-driven agent peer.
 *     Excluded from any user's UndoManager.
 *   - SYSTEM_ORIGIN: server-side reconciliation (orphan cleanup, default-value
 *     injection, post-merge dedupe). Excluded from every UndoManager.
 *   - HYDRATION_ORIGIN: the one-shot conversion of a legacy JSON graph into a
 *     fresh Y.Doc. Excluded so undo doesn't try to "undo" the hydration.
 */
export const AGENT_ORIGIN = Symbol('glue:collab:agent');
export const SYSTEM_ORIGIN = Symbol('glue:collab:system');
export const HYDRATION_ORIGIN = Symbol('glue:collab:hydration');

export const CURRENT_SCHEMA_VERSION = 1;

export function getNodesMap(yDoc: Y.Doc): Y.Map<Y.Map<unknown>> {
    return yDoc.getMap<Y.Map<unknown>>(NODES_KEY);
}

export function getConnectionsMap(yDoc: Y.Doc): Y.Map<Y.Map<unknown>> {
    return yDoc.getMap<Y.Map<unknown>>(CONNECTIONS_KEY);
}

export function getVariablesMap(yDoc: Y.Doc): Y.Map<Y.Map<unknown>> {
    return yDoc.getMap<Y.Map<unknown>>(VARIABLES_KEY);
}

export function getFunctionsMap(yDoc: Y.Doc): Y.Map<Y.Map<unknown>> {
    return yDoc.getMap<Y.Map<unknown>>(FUNCTIONS_KEY);
}

export function getMetaMap(yDoc: Y.Doc): Y.Map<unknown> {
    return yDoc.getMap<unknown>(META_KEY);
}

/** Read the graph name from the doc's meta map. `undefined` until it has been seeded. */
export function getGraphName(yDoc: Y.Doc): string | undefined {
    return getMetaMap(yDoc).get(META_NAME) as string | undefined;
}

/**
 * Write the graph name into the doc's meta map. Last-write-wins — the name is not
 * tracked by any UndoManager (`meta` is excluded), so renames don't enter undo history.
 * Uses the doc's clientID as transaction origin, matching other local-user writes.
 */
export function setGraphName(yDoc: Y.Doc, name: string): void {
    yDoc.transact(() => {
        getMetaMap(yDoc).set(META_NAME, name);
    }, yDoc.clientID);
}
